import {
  useIsSearchToFeedEnabled,
  useIsUnifiedFieldsEnabled,
} from '@modules/common/hooks';
import { formatNumToCompactNotation } from '@modules/common/utils';
import { ManageMetadataStarIconButton } from '@modules/integrationsV2/components/ManageMetadataSection/ManageMetadataStarIconButton';
import { ManageMetadataVisibilityToggleButton } from '@modules/integrationsV2/components/ManageMetadataSection/ManageMetadataVisibilityToggleButton';
import {
  IntegrationSource,
  ManageMetadataTableRow,
} from '@modules/integrationsV2/types';
import { getDisplayNameForMetadataType } from '@modules/integrationsV2/utils';
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { IntegrationType } from 'generated/graphql';
import pluralize from 'pluralize';
import { useMemo } from 'react';
import { ManageMetadataTableActions } from '../components/ManageMetadataSection/ManageMetadataTableActions';
import { MetadataUnifiedFieldsTooltip } from '../components/MetadataUnifiedFieldsTooltip';
interface GetManageMetadataTableColumnsProps {
  onEditButtonClick: (metadata: ManageMetadataTableRow) => void;
  onEditUnifiedFieldButtonClick?: (metadataKey?: string) => void;
  openCreateUnifiedFieldDrawer?: (metadataKey?: string) => void;
  integrationSource: IntegrationSource;
  shouldUpdateMetadataUnifiedFields?: boolean;
}

export const useGetManageMetadataTableColumns = ({
  onEditButtonClick,
  onEditUnifiedFieldButtonClick,
  integrationSource,
  openCreateUnifiedFieldDrawer,
  shouldUpdateMetadataUnifiedFields,
}: GetManageMetadataTableColumnsProps): GridColDef<ManageMetadataTableRow>[] => {
  const isUnifiedFieldsEnabled = useIsUnifiedFieldsEnabled();

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const isFeedbackIntegration =
    integrationSource.integrationType === IntegrationType.Feedback;

  return useMemo(() => {
    const { channel, namespace, integrationType, source } = integrationSource;

    const columns: GridColDef<ManageMetadataTableRow>[] = [
      {
        field: 'isImportant',
        headerName: 'Starred',
        headerAlign: 'center',
        type: 'boolean',
        minWidth: 90,
        display: 'flex',
        renderCell: (params) => {
          return (
            <ManageMetadataStarIconButton
              metadata={params.row}
              eqlSource={source ?? ''}
            />
          );
        },
        align: 'center',
      },
      {
        field: 'displayText',
        minWidth: 190,
        headerName: 'Name',
        flex: 1,
        display: 'flex',
        renderCell: (params) => (
          <>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                overflow: 'hidden',
                whiteSpace: 'normal',
                lineHeight: '16px',
                py: 0.5,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  whiteSpace: 'wrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.row.displayText}
              </Box>
              <Box>
                {isUnifiedFieldsEnabled && (
                  <>
                    {params.row.referencedIn?.key && (
                      <MetadataUnifiedFieldsTooltip
                        metadataKey={params.row.referencedIn.key ?? ''}
                        onEditUnifiedFieldButtonClick={
                          onEditUnifiedFieldButtonClick
                        }
                        shouldUpdateMetadataUnifiedFields={
                          shouldUpdateMetadataUnifiedFields
                        }
                      />
                    )}
                  </>
                )}
              </Box>
            </Stack>
          </>
        ),
      },
      {
        field: 'originalDisplayText',
        width: 160,
        headerName: 'Original Name',
        display: 'flex',
      },
      {
        field: 'type',
        headerName: 'Data Type',
        minWidth: 100,
        display: 'flex',
        renderCell: (params) => {
          return getDisplayNameForMetadataType(params.row.type);
        },
      },
      {
        field: 'cardinality',
        minWidth: 120,
        type: 'number',
        headerName: 'Unique Values',
        display: 'flex',
        renderCell: (params) => {
          return formatNumToCompactNotation({
            value: params.value,
            maximumFractionDigits: 1,
          });
        },
      },
      {
        field: 'coverage',
        headerName: 'Coverage',
        minWidth: 100,
        type: 'number',
        sortable: false,
        display: 'flex',
        renderCell: (params) => {
          const coverage = params.value;

          return coverage !== undefined ? (
            `${coverage}%`
          ) : (
            <Skeleton
              variant="rounded"
              width={30}
              height={16}
              sx={{
                display: 'inline-block',
                verticalAlign: 'middle',
                bgcolor: 'text.disabled',
              }}
            />
          );
        },
        renderHeader: () => {
          const integrationTypeLabel =
            integrationType === IntegrationType.Feedback
              ? 'feedback records'
              : pluralize(integrationType.toLowerCase());

          const sourceNameLabel = namespace
            ? `${namespace} - ${channel.displayName}`
            : channel.displayName;

          return (
            <Tooltip
              title={`Percentage of ${integrationTypeLabel} from ${sourceNameLabel} that have the populated metadata value.`}
              placement="top"
              arrow
            >
              <Typography variant="subtitle2">Coverage</Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'isHidden',
        headerName: 'Visibility',
        type: 'boolean',
        minWidth: 200,
        flex: 0.5,
        display: 'flex',
        renderCell: (params) => (
          <>
            <ManageMetadataVisibilityToggleButton metadata={params.row} />
            <ManageMetadataTableActions
              onEditButtonClick={onEditButtonClick}
              metadata={params.row}
              onEditUnifiedFieldButtonClick={onEditUnifiedFieldButtonClick}
              openCreateUnifiedFieldDrawer={openCreateUnifiedFieldDrawer}
            />
          </>
        ),
        renderHeader: () => (
          <Tooltip
            title={`Shows this metadata in the platform (in ${
              isSearchToFeedEnabled ? 'Feed' : 'Search'
            }, Quantify, Taxonomy, etc.)`}
            placement="top"
            arrow
          >
            <Typography variant="subtitle2">Visibility</Typography>
          </Tooltip>
        ),
      },
    ];

    if (!isFeedbackIntegration) {
      return columns.filter(
        (column) =>
          column.field !== 'coverage' && column.field !== 'isImportant',
      );
    }

    return columns;
  }, [
    integrationSource,
    isFeedbackIntegration,
    isSearchToFeedEnabled,
    isUnifiedFieldsEnabled,
    onEditButtonClick,
    onEditUnifiedFieldButtonClick,
    openCreateUnifiedFieldDrawer,
    shouldUpdateMetadataUnifiedFields,
  ]);
};
